import { twJoin } from "tailwind-merge"
import React, {useEffect, useMemo, useRef, useState} from "react"
import { Fade, SlideshowRef } from "react-slideshow-image"
import Image, { StaticImageData } from "next/image"
import Icon from "@components/UI/Icon"
import { SliderArrow } from "@components/SliderArrow/SliderArrow"

import slide0 from "@images/home-slides/troubles/slide_0.webp"
import slide1 from "@images/home-slides/troubles/slide_1.webp"
import slide2 from "@images/home-slides/troubles/slide_2.webp"
import slide3 from "@images/home-slides/troubles/slide_3.webp"
import slide4 from "@images/home-slides/troubles/slide_4.webp"

export const TroubleButton = ({
  icon,
  title,
  active,
  onClick,
}: {
  icon: string
  title: string
  active: boolean
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    className={twJoin(
      "flex flex-row whitespace-nowrap flex-nowrap items-center gap-x-8p",
      "border border-solid border-neutral2-400",
      "rounded-8p",
      "px-24p py-15p",
      "h-48p",
      "1440:h-64p",
      "transition-all duration-100",
      active
        ? [
            "!border-slate-blue-300",
            "bg-slate-blue-100",
          ]
        : undefined
    )}
  >
    <Icon
      name={icon}
      color="neutral-900"
      size={32}
      className={twJoin(
        active
          ? [
              "[&_path]:fill-slate-blue-500",
            ]
          : undefined
      )}
    />
    <span
      className={twJoin(
        "text-14p font-500 text-neutral2-900",
        active
          ? [
              "!text-slate-blue-500",
            ]
          : undefined
      )}
    >
      {title}
    </span>
  </button>
)

export type TroublesSliderProps = {
  title: string
  subtitle: string
  description: string
  buttonText: string
  imgSrc: StaticImageData
  imgAlt: string
  href: string
  domReady: boolean
  index: number
  onClick?: () => void
}

export const TroubleSlide = ({
  title,
  subtitle,
  description,
  buttonText,
  imgSrc,
  imgAlt,
  onClick,
  href,
  domReady,
  index,
}: TroublesSliderProps) => (
  <div
    className={twJoin(
      "relative",
      "flex flex-col-reverse items-center justify-center gap-24p",
      "768:flex-row",
      "768:gap-x-60p",
      "px-20p",
      "768:px-[unset]"
    )}
  >
    <div className={twJoin("flex flex-col gap-24p", " 768:w-3/5")}>
      <h2 className={twJoin("text-24p leading-32p font-700", "1440:text-40p 1440:leading-48p")}>{title}</h2>
      <h3 className={twJoin("text-16p font-400", "1440:text-24p 1440:leading-32p")}>{subtitle}</h3>
      <div className={twJoin("text-14p font-400", "1440:text-20p 1440:leading-28p")}>{description}</div>
      <div>
        {/* <MainButton
          size1024={56}
          size={44}
          className={twJoin("w-full", "768:w-fit")}
          href={href}
        >
          {buttonText}
        </MainButton> */}
      </div>
    </div>
    <div
      className={twJoin(
        "absolute z-10",
        "top-[23%]",
        "right-[20%]",
        "w-full max-w-[300px]",
        "rounded-full",
        "bg-[linear-gradient(95.96deg,_rgba(141,_110,_255,_0.4)_0%,_rgba(232,_57,_255,_0.2)_100.43%)]",
        "blur-[60px]"
      )}
    />
    <div className={twJoin("max-w-[320px] 480:max-w-[480px]", "768:w-2/5", "relative")}>
      <Image
        className={twJoin("relative z-20", "rounded-[366px] border-20p border-solid border-white")}
        src={imgSrc}
        alt={imgAlt}
        sizes='(min-width: 768px) 20vw, (min-width: 480px) 40vw, 100vw'
        style={{ display: index > 0 && !domReady ? "none" : "block" }}
      />
    </div>
  </div>
)

export const UseCasesSlider = () => {
  const [
    state,
    setState,
  ] = useState<{
    currentSlide: number
  }>({
    currentSlide: 0,
  })

  const sliderRef = useRef<SlideshowRef>(null)

  // TODO: может быть imgAlt вообще убрать
  const slides = useMemo(
    () => [
      {
        icon: "troubles-on-site",
        id: 0,
        href: "/use-cases/time-tracker-for-remote-teams",
        sectionButtonText: "Remote teams",
        title: "For remote teams",
        subtitle: "When work is distributed, the question is how it unfolds during the day.",
        description:
          "Traqq records tracked time and compares it with schedules and capacity. This surfaces overload and underwork without activity monitoring.",
        buttonText: "Discover how it works",
        imgSrc: slide0,
        imgAlt: "",
      },
      {
        icon: "troubles-small-business",
        id: 1,
        sectionButtonText: "Small & medium businesses",
        title: "For small and medium businesses",
        href: "/use-cases/time-tracking-software-for-small-business",
        subtitle: "In small and medium teams, time is often scattered across spreadsheets, tools, and reports.",
        description:
          "Traqq keeps time, reporting, and billing in one system. Work stays connected from entry to outcome.",
        buttonText: "Discover how it works",
        imgSrc: slide1,
        imgAlt: "",
      },
      {
        icon: "troubles-agencies",
        id: 2,
        sectionButtonText: "Agencies",
        title: "For agencies",
        href: "/use-cases/time-tracking-software-for-agencies",
        subtitle: "Client work is dynamic. Projects expand, slow down, and shift across people.",
        description:
          "Traqq tracks time with billing status at the point of entry. Costs and hours stay connected to the same record, so profitability is always visible, not reconstructed later.",
        buttonText: "Discover how it works",
        imgSrc: slide2,
        imgAlt: "",
      },
      {
        icon: "troubles-enterprises",
        id: 3,
        sectionButtonText: "Enterprises",
        title: "For enterprises",
        href: "/use-cases/enterprise-time-tracking-software",
        subtitle: "In larger organizations, different departments rarely define time in the same way. One team tracks utilization, another tracks delivery, a third focuses on cost allocation.",
        description:
          "Traqq standardizes time structure across teams so reporting, capacity, and financial data remain consistent. ",
        buttonText: "Discover how it works",
        imgSrc: slide3,
        imgAlt: "",
      },
      {
        icon: "troubles-freelancers",
        id: 4,
        href: "/use-cases/time-tracking-software-for-freelancers",
        sectionButtonText: "Freelancers & contractors",
        title: "For freelancers and contractors",
        subtitle: "Freelance work is often fragmented across clients and projects.",
        description:
          "Traqq keeps time and expenses tied to specific work items and generates client invoices directly from tracked entries.",
        buttonText: "Discover how it works",
        imgSrc: slide4,
        imgAlt: "",
      },
    ],
    []
  )

  const changeSlide = ({
    slideNumber,
    disableChangeSlide = false,
  }: {
    slideNumber: number
    disableChangeSlide?: boolean
  }) => {
    setState((prevState) => ({
      ...prevState,
      currentSlide: slideNumber,
    }))

    if (!disableChangeSlide) {
      sliderRef.current.goTo(slideNumber)
    }
  }

  const [domReady, setDomReady] = useState(false);

  useEffect(() => {
    setDomReady(true);
  }, []);

  return (
    <section
      className={twJoin(
        "flex flex-col gap-y-48p",
        "bg-slate-blue-50",
        "py-64p",
        "768:px-42p",
        "1440:px-[120px]",
        "1440:pt-80p",
        "1440:pb-120p"
      )}
    >
      <div className={twJoin("flex flex-col items-center gap-y-48p")}>
        <div className={twJoin("flex flex-col gap-y-24p", "px-20p")}>
          <h2 className={twJoin("text-26p", "1024:text-44p font-600 text-neutral2-900 text-center")}>
            Time tracking beyond timesheets
          </h2>
          <span className={twJoin("text-14p", "768:text-16p", "font-400 text-neutral2-800 text-center")}>
            Agencies focus on profitability. Managers balance workload and capacity. Growing companies need reliable reporting. Traqq handles all three in a single system.
          </span>
        </div>

        <div
          className={twJoin(
            "flex flex-row gap-x-16p justify-center flex-wrap gap-y-16p",
            "px-20p",
            "768:px-[unset]",
            // "1280:justify-center",
            "w-full",
            "max-w-[1280px]",
            "overflow-auto",
            "1440:overflow-visible"
          )}
          style={{
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
          }}
        >
          {slides.map((slide) => (
            <TroubleButton
              key={slide.id}
              active={state.currentSlide === slide.id}
              title={slide.sectionButtonText}
              icon={slide.icon}
              onClick={() => changeSlide({ slideNumber: slide.id })}
            />
          ))}
        </div>
        <div className="w-full max-w-[1200px] mx-auto">
          <Fade
            ref={sliderRef}
            autoplay={false}
            defaultIndex={0}
            duration={10}
            transitionDuration={100}
            onChange={(from, to) => {
              changeSlide({ slideNumber: to, disableChangeSlide: true })
            }}
            prevArrow={
              <button
                className={twJoin(
                  "!hidden",
                  "768:!flex",
                  "768:!left-[35%]",
                  "768:!bottom-[-25%]",
                  "1024:!bottom-[-18%]",
                  "1440:!bottom-[unset]",
                  "1440:!left-[-80px]"
                )}
              >
                <SliderArrow direction={"prev"} />
              </button>
            }
            nextArrow={
              <button
                className={twJoin(
                  "!hidden",
                  "768:!flex",
                  "768:!right-[35%]",
                  "768:!bottom-[-25%]",
                  "1024:!bottom-[-18%]",
                  "1440:!bottom-[unset]",
                  "1440:!right-[-80px]"
                )}
              >
                <SliderArrow direction={"next"} />
              </button>
            }
          >
            {slides.map((slide, index) => (
              <TroubleSlide
                key={slide.id}
                title={slide.title}
                subtitle={slide.subtitle}
                description={slide.description}
                buttonText={slide.buttonText}
                imgSrc={slide.imgSrc}
                imgAlt={slide.imgAlt}
                href={slide.href}
                domReady={domReady}
                index={index}
                onClick={() => {
                  changeSlide({ slideNumber: slide.id })
                }}
              />
            ))}
          </Fade>
        </div>
      </div>
    </section>
  )
}
